import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import MapHudButton from '../MapHudButton';
import { useWaterIntelRead } from '../../hooks/useWaterIntelRead';
import { getWaterIntelViewport, type WaterIntelViewportRead } from '../../services/waterIntel/waterIntelViewportClient';
import { buildWaterIntelEnvironment } from '../../services/waterIntel/waterIntelEnvironment';
import WaterIntelCard from './WaterIntelCard';

type Anchor = { lat: number; lng: number };

type Props = {
  isDarkMode: boolean;
  isActive: boolean;
  suppressLegend?: boolean;
  getMap: () => any | null;
  weatherData?: any;
  onLogCatch?: (target: Anchor) => void;
  onSearchFishIQ?: (target: Anchor) => void;
};

const CONTOURS = [10, 20, 50, 100, 200, 500, 1000, 2000];

function WaterContourIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-[23px] w-[23px]" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8.4 5.2c-2.4 2.8-4 4.8-4 7.2a4.5 4.5 0 0 0 9 0c0-2.4-1.8-4.8-5-7.2Z" />
      <path d="M13.5 6.4c2.5-.7 4.8-.3 6.1 1.2M14.9 10c2.2-.5 4.2-.2 5.4.8M14.7 14c2.1-.3 3.8.1 4.9 1" opacity=".72" />
    </svg>
  );
}

function depthColor(depth: number): [number, number, number, number] {
  const stops: Array<[number, [number, number, number]]> = [
    [0, [246, 224, 154]],
    [20, [151, 222, 185]],
    [50, [87, 207, 201]],
    [100, [62, 176, 205]],
    [250, [50, 137, 196]],
    [500, [58, 103, 177]],
    [1000, [75, 74, 151]],
    [2500, [45, 44, 101]],
  ];
  const d = Math.max(0, depth);
  let a = stops[0];
  let b = stops[stops.length - 1];
  for (let i = 1; i < stops.length; i += 1) {
    if (d <= stops[i][0]) {
      a = stops[i - 1];
      b = stops[i];
      break;
    }
  }
  const span = Math.max(1, b[0] - a[0]);
  const t = Math.max(0, Math.min(1, (d - a[0]) / span));
  return [
    Math.round(a[1][0] + (b[1][0] - a[1][0]) * t),
    Math.round(a[1][1] + (b[1][1] - a[1][1]) * t),
    Math.round(a[1][2] + (b[1][2] - a[1][2]) * t),
    210,
  ];
}

function rasterDataUrl(read: WaterIntelViewportRead): string | null {
  if (typeof document === 'undefined') return null;
  const src = document.createElement('canvas');
  src.width = read.cols;
  src.height = read.rows;
  const ctx = src.getContext('2d');
  if (!ctx) return null;
  const image = ctx.createImageData(read.cols, read.rows);

  for (let row = 0; row < read.rows; row += 1) {
    for (let col = 0; col < read.cols; col += 1) {
      const srcRow = read.rows - 1 - row;
      const depth = read.depthsMeters[srcRow * read.cols + col];
      const idx = (row * read.cols + col) * 4;
      if (typeof depth !== 'number' || !Number.isFinite(depth)) {
        image.data[idx + 3] = 0;
        continue;
      }
      const [r, g, b, a] = depthColor(depth);
      image.data[idx] = r;
      image.data[idx + 1] = g;
      image.data[idx + 2] = b;
      image.data[idx + 3] = a;
    }
  }
  ctx.putImageData(image, 0, 0);

  const out = document.createElement('canvas');
  out.width = 512;
  out.height = 512;
  const outCtx = out.getContext('2d');
  if (!outCtx) return src.toDataURL('image/png');
  outCtx.imageSmoothingEnabled = true;
  outCtx.drawImage(src, 0, 0, out.width, out.height);
  return out.toDataURL('image/png');
}

function interpolate(a: number, b: number, threshold: number) {
  if (a === b) return 0.5;
  return Math.max(0, Math.min(1, (threshold - a) / (b - a)));
}

function sampledDepthAt(read: WaterIntelViewportRead | null, lat: number, lng: number): number | null {
  if (!read) return null;
  const { south, west, north, east } = read.bounds;
  if (lat < south || lat > north || lng < west || lng > east) return null;

  const rowSpan = Math.max(1e-9, north - south);
  const colSpan = Math.max(1e-9, east - west);
  const row = Math.max(0, Math.min(read.rows - 1, Math.round(((lat - south) / rowSpan) * (read.rows - 1))));
  const col = Math.max(0, Math.min(read.cols - 1, Math.round(((lng - west) / colSpan) * (read.cols - 1))));
  const value = read.depthsMeters[row * read.cols + col];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function revealSelectedWaterPoint(map: any, target: Anchor) {
  try {
    const size = map?.getSize?.();
    const point = map?.latLngToContainerPoint?.([target.lat, target.lng]);
    if (!size || !point) return;

    // Keep the selected water dot in the clean lane between the top search HUD
    // and the bottom-snapped Water Intel card. This changes only the viewport;
    // the geographic target itself never changes.
    const desiredX = size.x * 0.5;
    const desiredY = Math.max(126, Math.min(210, size.y * 0.245));
    const offsetX = point.x - desiredX;
    const offsetY = point.y - desiredY;

    if (Math.abs(offsetX) < 2 && Math.abs(offsetY) < 2) return;
    map.panBy?.([offsetX, offsetY], { animate: true, duration: 0.28 });
  } catch {}
}

function eventHitRoddoControlOrMarker(event: any): boolean {
  const original = event?.originalEvent as MouseEvent | PointerEvent | undefined;
  const target = original?.target as Element | null | undefined;
  if (!target || typeof target.closest !== 'function') return false;

  return !!target.closest([
    'button',
    'a',
    '[role="button"]',
    '[data-roddo-polish-lane]',
    '.leaflet-control',
    '.leaflet-marker-icon',
    '.leaflet-marker-shadow',
    '.leaflet-popup',
    '.leaflet-tooltip',
    '.leaflet-interactive',
  ].join(','));
}

function contourSegments(read: WaterIntelViewportRead, threshold: number): Array<[Anchor, Anchor]> {
  const segments: Array<[Anchor, Anchor]> = [];
  const { south, west, north, east } = read.bounds;
  const latStep = (north - south) / Math.max(1, read.rows - 1);
  const lngStep = (east - west) / Math.max(1, read.cols - 1);
  const depthAt = (r: number, c: number) => read.depthsMeters[r * read.cols + c];

  for (let r = 0; r < read.rows - 1; r += 1) {
    for (let c = 0; c < read.cols - 1; c += 1) {
      const sw = depthAt(r, c);
      const se = depthAt(r, c + 1);
      const nw = depthAt(r + 1, c);
      const ne = depthAt(r + 1, c + 1);
      if ([sw, se, nw, ne].some((v) => typeof v !== 'number' || !Number.isFinite(v as number))) continue;

      const points: Anchor[] = [];
      const lat0 = south + r * latStep;
      const lat1 = south + (r + 1) * latStep;
      const lng0 = west + c * lngStep;
      const lng1 = west + (c + 1) * lngStep;

      const crosses = (a: number, b: number) => (a < threshold && b >= threshold) || (a >= threshold && b < threshold);
      if (crosses(sw as number, se as number)) {
        const t = interpolate(sw as number, se as number, threshold);
        points.push({ lat: lat0, lng: lng0 + (lng1 - lng0) * t });
      }
      if (crosses(se as number, ne as number)) {
        const t = interpolate(se as number, ne as number, threshold);
        points.push({ lat: lat0 + (lat1 - lat0) * t, lng: lng1 });
      }
      if (crosses(nw as number, ne as number)) {
        const t = interpolate(nw as number, ne as number, threshold);
        points.push({ lat: lat1, lng: lng0 + (lng1 - lng0) * t });
      }
      if (crosses(sw as number, nw as number)) {
        const t = interpolate(sw as number, nw as number, threshold);
        points.push({ lat: lat0 + (lat1 - lat0) * t, lng: lng0 });
      }

      if (points.length === 2) segments.push([points[0], points[1]]);
      if (points.length === 4) {
        segments.push([points[0], points[1]], [points[2], points[3]]);
      }
    }
  }
  return segments;
}

export default function WaterIntelDock({
  isDarkMode,
  isActive,
  suppressLegend = false,
  getMap,
  weatherData,
  onLogCatch,
  onSearchFishIQ,
}: Props) {
  const [enabled, setEnabled] = useState(false);
  const [viewportRead, setViewportRead] = useState<WaterIntelViewportRead | null>(null);
  const [viewportLoading, setViewportLoading] = useState(false);
  const [coverageAvailable, setCoverageAvailable] = useState(false);
  const [target, setTarget] = useState<Anchor | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [radioBottomSurfaceVisible, setRadioBottomSurfaceVisible] = useState(false);
  const [mapRect, setMapRect] = useState<DOMRect | null>(null);
  const dockRef = useRef<HTMLDivElement | null>(null);
  const overlayGroupRef = useRef<L.LayerGroup | null>(null);
  const targetGroupRef = useRef<L.LayerGroup | null>(null);
  const refreshTimerRef = useRef<number | null>(null);
  const coverageTimerRef = useRef<number | null>(null);
  const lastMapRectRef = useRef<{ width: number; height: number } | null>(null);
  const cardOpenRef = useRef(false);
  const targetRef = useRef<Anchor | null>(null);
  const loadSeqRef = useRef(0);
  const coverageSeqRef = useRef(0);
  const { read, loading, error, load, clear } = useWaterIntelRead();

  const environment = useMemo(
    () => buildWaterIntelEnvironment(weatherData, target),
    [weatherData, target],
  );

  useEffect(() => {
    cardOpenRef.current = cardOpen;
  }, [cardOpen]);

  useEffect(() => {
    targetRef.current = target;
  }, [target]);

  useEffect(() => {
    const onRadioVisibility = (event: Event) => {
      const detail = (event as CustomEvent<{ visible?: boolean }>).detail;
      setRadioBottomSurfaceVisible(detail?.visible === true);
    };

    window.addEventListener('roddo-radio-map-visibility', onRadioVisibility as EventListener);
    return () => window.removeEventListener('roddo-radio-map-visibility', onRadioVisibility as EventListener);
  }, []);

  useEffect(() => {
    const map = getMap();
    const container = map?.getContainer?.() as HTMLElement | undefined;
    if (!container) {
      setMapRect(null);
      return;
    }

    let raf = 0;
    const syncRect = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        try {
          const next = container.getBoundingClientRect();
          setMapRect(next);

          const previous = lastMapRectRef.current;
          const sizeChanged =
            !previous ||
            Math.abs(previous.width - next.width) > 1 ||
            Math.abs(previous.height - next.height) > 1;

          if (sizeChanged) {
            lastMapRectRef.current = { width: next.width, height: next.height };
            map?.invalidateSize?.({ pan: false, debounceMoveend: true });

            const activeTarget = targetRef.current;
            if (cardOpenRef.current && activeTarget) {
              revealSelectedWaterPoint(map, activeTarget);
            }
          }
        } catch {}
      });
    };

    syncRect();

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(syncRect)
      : null;
    observer?.observe(container);
    window.addEventListener('resize', syncRect);
    window.addEventListener('orientationchange', syncRect);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', syncRect);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener('resize', syncRect);
      window.removeEventListener('orientationchange', syncRect);
      vv?.removeEventListener('resize', syncRect);
    };
  }, [getMap]);

  const removeOverlay = useCallback(() => {
    const map = getMap();
    if (!map) return;
    if (overlayGroupRef.current) {
      try { map.removeLayer(overlayGroupRef.current); } catch {}
      overlayGroupRef.current = null;
    }
  }, [getMap]);

  const drawOverlay = useCallback((next: WaterIntelViewportRead | null) => {
    const map = getMap();
    if (!map) return;
    if (!next || next.waterCellCount <= 0) {
      removeOverlay();
      return;
    }

    if (!map.getPane('roddo-water-intel-pane')) {
      const pane = map.createPane('roddo-water-intel-pane');
      pane.style.zIndex = '330';
      pane.style.pointerEvents = 'none';
    }

    // Build the replacement group while the current overlay stays attached.
    // That keeps Leaflet's existing georeferenced raster moving smoothly during
    // a drag and avoids the visible clear/repaint gap after moveend.
    const layers: L.Layer[] = [];
    const url = rasterDataUrl(next);
    if (url) {
      layers.push(
        L.imageOverlay(
          url,
          [[next.bounds.south, next.bounds.west], [next.bounds.north, next.bounds.east]],
          { opacity: isDarkMode ? 0.62 : 0.56, pane: 'roddo-water-intel-pane', interactive: false },
        ),
      );
    }

    // One multi-polyline per contour threshold instead of one Leaflet layer per
    // marching-squares segment. This cuts normal overlay layer churn from
    // hundreds of vector layers down to at most CONTOURS.length.
    CONTOURS.forEach((threshold) => {
      if ((next.maxDepthMeters ?? 0) < threshold || (next.minDepthMeters ?? Infinity) > threshold) return;
      const segments = contourSegments(next, threshold);
      if (!segments.length) return;

      const paths = segments.map(([a, b]) => [[a.lat, a.lng], [b.lat, b.lng]]);
      layers.push(
        L.polyline(paths as any, {
          pane: 'roddo-water-intel-pane',
          interactive: false,
          weight: threshold >= 500 ? 1.25 : 1,
          opacity: isDarkMode ? 0.56 : 0.48,
          color: isDarkMode ? '#E9E3FF' : '#25384D',
          smoothFactor: 1.25,
          noClip: false,
        }),
      );
    });

    const nextGroup = L.layerGroup(layers);
    nextGroup.addTo(map);

    const previousGroup = overlayGroupRef.current;
    overlayGroupRef.current = nextGroup;

    if (previousGroup && previousGroup !== nextGroup) {
      try { map.removeLayer(previousGroup); } catch {}
    }
  }, [getMap, isDarkMode, removeOverlay]);

  const probeCoverage = useCallback(async () => {
    const map = getMap();
    if (!map || !isActive || enabled) return;
    const bounds = map.getBounds?.();
    if (!bounds) return;

    const south = Number(bounds.getSouth());
    const west = Number(bounds.getWest());
    const north = Number(bounds.getNorth());
    const east = Number(bounds.getEast());
    if (![south, west, north, east].every(Number.isFinite)) return;

    // Keep the Water Intel affordance honest: only show it when the current
    // viewport intersects actual loaded bathymetry coverage. Wide/global views
    // intentionally hide the control until the angler moves back into coverage.
    if (north - south > 6 || east - west > 6) {
      setCoverageAvailable(false);
      return;
    }

    const seq = ++coverageSeqRef.current;
    try {
      const next = await getWaterIntelViewport({ south, west, north, east, rows: 20, cols: 20 });
      if (seq !== coverageSeqRef.current) return;
      setCoverageAvailable(Boolean(next && next.waterCellCount > 0));
    } catch {
      if (seq === coverageSeqRef.current) setCoverageAvailable(false);
    }
  }, [enabled, getMap, isActive]);

  const loadViewport = useCallback(async () => {
    const map = getMap();
    if (!map || !enabled) return;
    const bounds = map.getBounds?.();
    if (!bounds) return;
    const south = Number(bounds.getSouth());
    const west = Number(bounds.getWest());
    const north = Number(bounds.getNorth());
    const east = Number(bounds.getEast());
    if (![south, west, north, east].every(Number.isFinite)) return;

    if (north - south > 6 || east - west > 6) {
      setViewportRead(null);
      setCoverageAvailable(false);
      setCardOpen(false);
      setTarget(null);
      setEnabled(false);
      removeOverlay();
      clear();
      return;
    }

    const seq = ++loadSeqRef.current;
    setViewportLoading(true);
    try {
      const next = await getWaterIntelViewport({ south, west, north, east, rows: 32, cols: 32 });
      if (seq !== loadSeqRef.current) return;
      const hasCoverage = Boolean(next && next.waterCellCount > 0);
      setCoverageAvailable(hasCoverage);
      setViewportRead(next);
      drawOverlay(next);
      if (!hasCoverage) {
        setCardOpen(false);
        setTarget(null);
        setEnabled(false);
        clear();
      }
    } catch {
      if (seq === loadSeqRef.current) {
        // Keep the last good overlay/read visible on a transient viewport fetch
        // failure. The next settled pan/zoom can retry without flashing Big Blue.
        setCoverageAvailable((prev) => prev || Boolean(viewportRead?.waterCellCount));
      }
    } finally {
      if (seq === loadSeqRef.current) setViewportLoading(false);
    }
  }, [clear, drawOverlay, enabled, getMap, removeOverlay, viewportRead]);

  useEffect(() => {
    const map = getMap();
    if (!map || !isActive || enabled) return;

    const scheduleProbe = () => {
      if (coverageTimerRef.current != null) window.clearTimeout(coverageTimerRef.current);
      coverageTimerRef.current = window.setTimeout(() => void probeCoverage(), 220);
    };

    scheduleProbe();
    map.on('moveend', scheduleProbe);
    map.on('zoomend', scheduleProbe);
    return () => {
      map.off('moveend', scheduleProbe);
      map.off('zoomend', scheduleProbe);
      if (coverageTimerRef.current != null) window.clearTimeout(coverageTimerRef.current);
      coverageTimerRef.current = null;
    };
  }, [enabled, getMap, isActive, probeCoverage]);

  useEffect(() => {
    if (!isActive && enabled) setEnabled(false);
  }, [enabled, isActive]);

  useEffect(() => {
    const map = getMap();
    if (!map || !enabled) {
      removeOverlay();
      return;
    }

    void loadViewport();
    const onMoveEnd = () => {
      if (refreshTimerRef.current != null) window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = window.setTimeout(() => void loadViewport(), 140);
    };
    const onClick = (event: any) => {
      // Existing Roddo controls/markers always own their click. Water Intel is
      // only an inspector for otherwise-empty water on the base map.
      if (eventHitRoddoControlOrMarker(event)) return;

      const lat = Number(event?.latlng?.lat);
      const lng = Number(event?.latlng?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      // The viewport raster uses null for land/no-data. Require a real sampled
      // bathymetry cell before opening a point read so taps on streets, shore,
      // islands, or unloaded coverage do not create misleading water cards.
      if (sampledDepthAt(viewportRead, lat, lng) == null) return;

      const nextTarget = { lat, lng };
      setTarget(nextTarget);
      setCardOpen(true);
      revealSelectedWaterPoint(map, nextTarget);
      void load(nextTarget);
    };

    map.on('moveend', onMoveEnd);
    map.on('click', onClick);
    return () => {
      map.off('moveend', onMoveEnd);
      map.off('click', onClick);
      if (refreshTimerRef.current != null) window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
      removeOverlay();
    };
  }, [enabled, getMap, load, loadViewport, removeOverlay, viewportRead]);


  useEffect(() => {
    const map = getMap();
    if (!map) return;
    if (targetGroupRef.current) {
      try { map.removeLayer(targetGroupRef.current); } catch {}
      targetGroupRef.current = null;
    }
    if (!enabled || !target) return;

    const reopenSelectedWaterPoint = () => {
      setCardOpen(true);
      revealSelectedWaterPoint(map, target);

      // Closing the card intentionally keeps the selected POI + cached read.
      // Reopening the same POI should therefore be instant. Only issue a new
      // read when there is not already data/loading for this target.
      if (!read && !loading) {
        void load(target);
      }
    };

    const outerTargetMarker = L.circleMarker([target.lat, target.lng], {
      radius: 10,
      weight: 2.25,
      color: isDarkMode ? '#F4F7FF' : '#FFF9EF',
      fillColor: '#66D9E8',
      fillOpacity: 0.12,
    });

    const innerTargetMarker = L.circleMarker([target.lat, target.lng], {
      radius: 4.25,
      weight: 2,
      color: '#66D9E8',
      fillColor: isDarkMode ? '#0A1220' : '#FFF9EF',
      fillOpacity: 0.98,
    });

    outerTargetMarker.on('click', reopenSelectedWaterPoint);
    innerTargetMarker.on('click', reopenSelectedWaterPoint);

    const group = L.layerGroup([
      outerTargetMarker,
      innerTargetMarker,
    ]).addTo(map);
    targetGroupRef.current = group;

    return () => {
      try { map.removeLayer(group); } catch {}
      if (targetGroupRef.current === group) targetGroupRef.current = null;
    };
  }, [enabled, getMap, isDarkMode, load, loading, read, target]);

  useEffect(() => () => {
    if (coverageTimerRef.current != null) window.clearTimeout(coverageTimerRef.current);
    coverageTimerRef.current = null;
    removeOverlay();
    const map = getMap();
    if (map && targetGroupRef.current) {
      try { map.removeLayer(targetGroupRef.current); } catch {}
    }
  }, [getMap, removeOverlay]);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      if (!next) {
        setCardOpen(false);
        setTarget(null);
        setViewportRead(null);
        clear();
      }
      return next;
    });
  }, [clear]);

  if (!isActive || !coverageAvailable) return null;

  const map = getMap();
  const rect = mapRect;

  const dockRect = dockRef.current?.getBoundingClientRect?.() ?? null;
  const compactViewport = Boolean(rect && rect.width < 900);
  const bottomNavHeight = typeof document !== 'undefined'
    ? Number.parseFloat(
        window.getComputedStyle(document.documentElement)
          .getPropertyValue('--roddo-bottom-nav-h') || '84',
      ) || 84
    : 84;
  const legendBaseBottom = rect
    ? Math.max(10, window.innerHeight - rect.bottom + 10)
    : 10;
  const legendBottom = compactViewport
    ? legendBaseBottom + bottomNavHeight + 12
    : legendBaseBottom;

  // Keep the bathymetry key down on the map floor instead of beside the Water
  // Intel button. While a point-read card is open the card already explains
  // depth/structure, so suppress the duplicate key entirely.
  const legend =
    enabled &&
    viewportRead &&
    viewportRead.waterCellCount > 0 &&
    !cardOpen &&
    !suppressLegend &&
    !radioBottomSurfaceVisible &&
    typeof document !== 'undefined' &&
    rect
      ? createPortal(
          <div
            className="fixed z-[7150] pointer-events-none"
            style={{
              left: Math.max(rect.left + 72, Math.min(rect.right - 188, rect.left + 76)),
              bottom: legendBottom,
            }}
            aria-hidden="true"
          >
            <div
              className="rounded-[13px] border px-2.5 py-2 shadow-lg backdrop-blur-xl"
              style={{
                width: 176,
                background: isDarkMode ? 'rgba(10,18,32,.92)' : 'rgba(255,249,239,.96)',
                color: isDarkMode ? '#F2F6FC' : '#34424C',
                borderColor: isDarkMode ? 'rgba(132,151,190,.20)' : 'rgba(111,87,60,.14)',
                boxShadow: isDarkMode ? '0 8px 22px rgba(0,0,0,.26)' : '0 8px 22px rgba(89,65,37,.13)',
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[8px] font-black uppercase tracking-[0.16em] opacity-75">Depth</span>
                <span className="text-[7.5px] font-bold opacity-50">GEBCO · fishing ref.</span>
              </div>
              <div
                className="mt-1.5 h-[7px] w-full rounded-full border border-black/5 dark:border-white/10"
                style={{ background: 'linear-gradient(90deg,#f6e09a 0%,#97deb9 15%,#57cfc9 28%,#3eb0cd 42%,#3289c4 58%,#3a67b1 72%,#4b4a97 86%,#2d2c65 100%)' }}
              />
              <div className="mt-1 flex items-center justify-between text-[7.5px] font-extrabold tabular-nums opacity-65">
                <span>0m</span><span>50</span><span>100</span><span>250</span><span>500m+</span>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;
  const desktopAnchored = !!(rect && rect.width >= 900 && dockRect);
  const panelGap = 12;
  const panelWidth = 360;
  const panelMargin = 12;
  const panelMaxHeight = rect
    ? Math.max(320, Math.min(720, rect.height - panelMargin * 2))
    : undefined;

  let panelStyle: React.CSSProperties = {};
  if (desktopAnchored && rect && dockRect) {
    const minLeft = rect.left + panelMargin;
    const maxLeft = Math.max(minLeft, rect.right - panelWidth - panelMargin);
    const left = Math.max(minLeft, Math.min(maxLeft, dockRect.right + panelGap));

    // Grow the Water Intel card UP from the HUD button instead of DOWN from it.
    // The button sits low in Big Blue's left stack, so a top-anchored tall card
    // falls off the bottom of the map. Bottom-aligning to the button keeps the
    // whole read visible and matches Saved Spots/FishIQ desktop composition.
    const mapBottomClearance = Math.max(panelMargin, window.innerHeight - rect.bottom + panelMargin);
    const dockBottomClearance = Math.max(panelMargin, window.innerHeight - dockRect.bottom);
    const bottom = Math.max(mapBottomClearance, dockBottomClearance);

    panelStyle = { left, bottom };
  } else if (rect) {
    const panelBottomGap = Math.max(92, Math.min(108, rect.height * 0.115));
    panelStyle = {
      left: Math.max(192, Math.min(window.innerWidth - 192, rect.left + rect.width / 2)),
      bottom: Math.max(10, window.innerHeight - rect.bottom + panelBottomGap),
      transform: 'translateX(-50%)',
    };
  }

  const panel = cardOpen && typeof document !== 'undefined' && rect
    ? createPortal(
        <div
          className="fixed z-[7200] pointer-events-auto"
          style={panelStyle}
        >
          <WaterIntelCard
            read={read}
            loading={loading}
            error={error}
            isDarkMode={isDarkMode}
            environment={environment}
            target={target}
            maxHeightPx={desktopAnchored ? panelMaxHeight : undefined}
            onClose={() => setCardOpen(false)}
            onRefresh={() => target && void load(target)}
            onLogCatch={onLogCatch ? (nextTarget) => {
              setCardOpen(false);
              onLogCatch(nextTarget);
            } : undefined}
            onSearchFishIQ={onSearchFishIQ ? (nextTarget) => {
              setCardOpen(false);
              onSearchFishIQ(nextTarget);
            } : undefined}
          />
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <div ref={dockRef} className="relative">
        <MapHudButton
          icon={<WaterContourIcon />}
          label={enabled ? 'Turn off Water Intel' : 'Water Intel'}
          active={enabled}
          onClick={toggle}
          className={enabled ? 'text-[#24779B] dark:text-[#BDEFF5]' : 'text-[#24779B] dark:text-[#BDEFF5]'}
        />
        {enabled && viewportRead && viewportRead.waterCellCount <= 0 ? (
          <span className="sr-only">No loaded bathymetry coverage in this view.</span>
        ) : null}
        {legend}
      </div>
      {panel}
    </>
  );
}
